import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ScheduleHolidaysProvider } from "@/components/team/Holidays/schedule/ScheduleHolidaysProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useWeekViewContext } from "@/contexts/WeekView";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { NEUTRAL } from "@/theme/tokens";
import { EmptyState } from "@/ui/molecules/EmptyState";
import { toIsoDate } from "@/utils/scheduleWeeks";
import { staffingWeeklyPath } from "@/utils/staffingRoutes";
import { MobileGapsStaffing } from "./MobileGapsStaffing";
import { useGapsVisibleWeek } from "./useGapsVisibleWeek";

/** What the shell says instead of the home, when it says anything. */
enum RosterNotice {
	None = "none",
	NoStaff = "no_staff",
	Failed = "failed",
}

/**
 * What to say instead of the home when there is no roster to build it from. `WeekViewProvider` builds no day at
 * all without staff, so the home would be its chrome around nothing. A roster that is still there after a later
 * refetch fails keeps the home: the data on screen is still the department's.
 */
const rosterNoticeFor = (staffCount: number, isRosterLoaded: boolean, isRosterFailed: boolean): RosterNotice => {
	if (staffCount > 0) return RosterNotice.None;
	if (isRosterFailed) return RosterNotice.Failed;
	return isRosterLoaded ? RosterNotice.NoStaff : RosterNotice.None;
};

/**
 * The gaps-first mobile Staffing shell: the day's content, above a fixed bottom bar.
 *
 * The shell owns the URL date; `MobileGapsStaffing` owns what is drawn, including which part scrolls and
 * where pull-to-refresh lives. It drops classic's `MobileStaffingHeader` and `StaffingActionFab`: the bottom
 * bar owns navigation now, and the FAB's actions moved into the More sheet, so keeping either would
 * leave two of everything on one phone screen.
 */
export const MobileGapsShell: FC = () => {
	const { dateString } = useParams();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { firstDate, lastDate } = useWeekViewContext();
	// Read for its outcome too: an empty roster is only "no staff" when the load SUCCEEDED — a failed one (a 403, the
	// network) must not claim the department has nobody.
	const { roster, nurses, isError: isRosterFailed } = useRosterLookup(chosenDepartment.department_id);
	const rosterNotice = rosterNoticeFor(nurses.length, roster !== undefined, isRosterFailed);
	const { isShownDay, firstDay, lastDay } = useGapsVisibleWeek();

	const selectedDate = useMemo<DateTime>(() => {
		const fromUrl = dateString === undefined ? undefined : DateTime.fromISO(dateString);
		// DEFAULT: an absent or unparseable URL date opens today. The date is a deep-link convenience, not
		// data — a bad one means the link was mistyped or stale, and today is the day a head nurse opening
		// their phone is asking about. Rendering nothing instead would hide a working schedule behind a typo.
		return fromUrl?.isValid ? fromUrl : DateTime.now();
	}, [dateString]);

	const handleDateChange = useCallback(
		(next: DateTime): void => {
			navigate(staffingWeeklyPath(chosenDepartment.department_id, next.toFormat("yyyy-MM-dd")));
		},
		[navigate, chosenDepartment.department_id],
	);

	// A valid date the schedule does not cover — a shared link, a stale notification deep link, a bookmark from last
	// month (flag off, any day outside the old list's loaded weeks) — has no week to show. Redirect onto the nearest day
	// it does cover, mirroring what the classic screen does for the same URLs. That day is inside the range, so this
	// cannot re-fire. Nothing happens while the range is unknown.
	//
	// With weeks starting after Sunday, the schedule's first days before the start day belong to a visible week that
	// begins before it, so the earliest day to land on is `firstDay`.
	useEffect(() => {
		if (firstDay === undefined || lastDay === undefined || isShownDay(selectedDate)) return;
		const clamped = toIsoDate(selectedDate) < toIsoDate(firstDay) ? firstDay : lastDay;
		navigate(staffingWeeklyPath(chosenDepartment.department_id, toIsoDate(clamped)), { replace: true });
	}, [firstDay, lastDay, isShownDay, selectedDate, navigate, chosenDepartment.department_id]);

	return (
		// No scroller here: the page pins its chrome and scrolls only its list (`GapsListScroller`), which is
		// also where pull-to-refresh lives. No bottom-bar clearance either: `Layout` reserves it for every
		// route at once, because the bar is mounted there and covers pages this one knows nothing about.
		<Box data-testid="gaps-home-root" sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, width: "100%", bgcolor: NEUTRAL[50] }}>
			{rosterNotice === RosterNotice.Failed ? (
				<EmptyState
					dataTestId="gaps-home-roster-failed"
					icon={<ErrorOutlineOutlinedIcon />}
					iconColor="error.main"
					title={t("gaps.empty.rosterFailed")}
					hint={t("gaps.empty.rosterFailedHint")}
					sx={{ py: 8, px: 3 }}
				/>
			) : rosterNotice === RosterNotice.NoStaff ? (
				<EmptyState
					dataTestId="gaps-home-no-staff"
					icon={<GroupOutlinedIcon />}
					title={t("gaps.empty.noStaff")}
					hint={t("gaps.empty.noStaffHint")}
					sx={{ py: 8, px: 3 }}
				/>
			) : (
				// Keyed by department so a switch starts from a clean screen — the filter, tab and open groups are one
				// department's. `HospitalDataProvider` already remounts everything while a new department loads; the key
				// keeps that true here without relying on its loading screen.
				<ScheduleHolidaysProvider firstDate={firstDate} lastDate={lastDate}>
					<MobileGapsStaffing key={chosenDepartment.department_id} date={selectedDate} onDateChange={handleDateChange} />
				</ScheduleHolidaysProvider>
			)}
		</Box>
	);
};
